import { useCallback, useMemo } from 'react';
import { useUserContext } from '@blocs.auth';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { useTranslation } from '@blocs.i18n';
import { PaywallLocation, PurchaseResultType, useBillingPopup } from '@blocs.paywall';
import { useModal } from '@minecraft.modal';
import { useRouteHelper } from '@consumer-talent.utils/routeHelper';
import { useArtistContext } from '../core/ArtistContext';

/**
 * Owns the paid-trial headshots upsell: eligibility, open/close state, the
 * formatted trial end date, and the upgrade callback. The modal stays presentational.
 */
export const useHeadshotTrialModal = () => {
  const { isOpen, open, close } = useModal();
  const { isPaidTrialSubscriber, myAccount } = useArtistContext();
  const { subscription } = useUserContext();
  const { formatIntlDate } = useTranslation();
  const billingPopup = useBillingPopup();
  const { push } = useRouteHelper();
  const headshotsDirectBooking = useFeatureExperiment(FEATURE_FLAGS.WEB_HEADSHOTS_DIRECT_BOOKING);
  const isTrialUpsellEligible = Boolean(headshotsDirectBooking) && Boolean(isPaidTrialSubscriber);

  const trialEndDate = formatIntlDate({
    date: subscription?.trialEndsAt,
    format: 'MONTH_DAY_YEAR',
    timeZoneStandardName: myAccount?.timeZone?.standardName,
  });

  const openTrialModal = useCallback(() => {
    if (!isTrialUpsellEligible) {
      return;
    }

    open();
  }, [isTrialUpsellEligible, open]);

  const handleTrialUpgrade = useCallback(async () => {
    captureHeapEvent({
      name: HEAP_EVENT_NAMES.TALENT_CLICK_HEADSHOTS_TRIAL_UPGRADE_MODULE_UPGRADE_NOW,
    });
    close();

    const { getPurchaseResult } = billingPopup.show({
      location: PaywallLocation.HEADSHOTS_TRIAL,
    });

    // A paused subscription opens the resume modal and does not return a purchase promise.
    if (!getPurchaseResult) {
      return;
    }

    // This await lives on the nav, which stays mounted after the trial modal closes.
    // BillingPopupProvider resolves the promise when the paywall closes. It rejects
    // with "Component unmounted" only if that provider itself unmounts first.
    const purchaseResult = await getPurchaseResult();

    if (purchaseResult.type === PurchaseResultType.Success) {
      push('headshots');
    }
  }, [billingPopup, close, push]);

  return useMemo(
    () => ({
      headshotsDirectBooking: Boolean(headshotsDirectBooking),
      isTrialUpsellEligible,
      isTrialModalOpen: isOpen,
      openTrialModal,
      closeTrialModal: close,
      trialEndDate,
      handleTrialUpgrade,
    }),
    [close, handleTrialUpgrade, headshotsDirectBooking, isOpen, isTrialUpsellEligible, openTrialModal, trialEndDate]
  );
};

export type UseHeadshotTrialModalValues = ReturnType<typeof useHeadshotTrialModal>;
